import { useState } from "react";
import { Calendar, Plus } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { format, startOfDay, isBefore, isAfter, isEqual } from "date-fns";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useScrollPreservation } from "@/hooks/use-scroll-preservation";

interface TaggedDate {
  id?: string;
  tag: string;
  date: string;
  color: string;
}

interface TaggableDateCellProps {
  entityType: 'project' | 'sequence';
  entityId: string | number;
  taggedDates: TaggedDate[];
  projectStatus?: string;
  taskStatus?: string;
}

const tagOptions = [
  { label: "Deadline", value: "deadline", color: "bg-[#6f88bf]" },
  { label: "Due Date", value: "due-date", color: "bg-[#6f88bf]" },
  { label: "Feedback", value: "feedback", color: "bg-[#6f88bf]" },
  { label: "Briefing", value: "briefing", color: "bg-[#425272]" },
  { label: "Preview", value: "preview", color: "bg-[#6f88bf]" },
  { label: "Start", value: "start", color: "bg-[#6f88bf]" },
  { label: "Event", value: "event", color: "bg-[#425272]" },
  { label: "Setup", value: "setup", color: "bg-[#425272]" },
  { label: "Travel", value: "travel", color: "bg-[#425272]" },
];

// Function to get dynamic color based on current date, project status, and task status
const getDynamicColor = (taggedDate: TaggedDate, projectStatus?: string, taskStatus?: string) => {
  const today = startOfDay(new Date());
  const dateToCheck = startOfDay(new Date(taggedDate.date));
  
  // Calculate days until deadline
  const daysDifference = Math.ceil((dateToCheck.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  
  // If project status is "final" (completed) OR task status is "done", show deadline and due-date in green
  if ((projectStatus === "final" || taskStatus === "done") && (taggedDate.tag === "deadline" || taggedDate.tag === "due-date")) {
    return "bg-emerald-600"; // Green for completed projects/tasks
  }
  
  // For deadline and due-date tags, apply dynamic coloring based on urgency
  if (taggedDate.tag === "deadline" || taggedDate.tag === "due-date") {
    // If within 3 days (including past due, due today, and next 2 days) and not final/done
    if (daysDifference <= 3 && projectStatus !== "final" && taskStatus !== "done") {
      return "bg-[#aa6246]"; // Orange for urgent deadlines
    }
  }
  
  // Use the tag's defined color from tagOptions, fallback to default blue
  const tagOption = tagOptions.find(opt => opt.value === taggedDate.tag);
  return tagOption?.color || "bg-[#6f88bf]";
};

export function TaggableDateCell({ entityType, entityId, taggedDates, projectStatus, taskStatus }: TaggableDateCellProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [newTag, setNewTag] = useState("deadline");
  const [newDate, setNewDate] = useState("");
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { captureScroll, restoreScroll } = useScrollPreservation();

  const updateMutation = useMutation({
    mutationFn: async (updatedDates: TaggedDate[]) => {
      // Capture scroll position before update
      captureScroll();
      
      const endpoint = entityType === 'project' 
        ? `/api/projects/${entityId}`
        : `/api/sequences/${entityId}`;
      
      return await apiRequest('PATCH', endpoint, { taggedDates: updatedDates });
    },
    onSuccess: async () => {
      // Show success toast
      toast({
        title: "Success",
        description: `${entityType.charAt(0).toUpperCase() + entityType.slice(1)} timing updated successfully`,
      });
      
      // Invalidate relevant queries based on entity type
      const queries = entityType === 'project' 
        ? ["/api/projects", "/api/years"]
        : ["/api/sequences", "/api/projects", "/api/years"];
      
      await Promise.all(
        queries.map(key => 
          queryClient.invalidateQueries({ 
            queryKey: [key],
            refetchType: 'all' 
          })
        )
      );
      
      // Force refetch to ensure fresh data
      await Promise.all([
        queryClient.refetchQueries({ queryKey: ["/api/projects"] }),
        queryClient.refetchQueries({ queryKey: ["/api/years"] })
      ]);
      
      // Restore scroll position after updates
      restoreScroll();
    },
    onError: () => {
      toast({
        title: "Error", 
        description: `Failed to update ${entityType} timing`,
        variant: "destructive",
      });
      
      // Restore scroll position even on error
      restoreScroll();
    },
  });

  const handleAddDate = () => {
    if (!newTag || !newDate) return;
    
    const tagOption = tagOptions.find(option => option.value === newTag);
    if (!tagOption) return;

    const newTaggedDate: TaggedDate = {
      ...(entityType === "project" ? { id: crypto.randomUUID() } : {}),
      tag: newTag,
      date: newDate,
      color: tagOption.color,
    };

    const updatedDates = [...(taggedDates || []), newTaggedDate];
    updateMutation.mutate(updatedDates);
    setNewTag("deadline");
    setNewDate("");
    // Close dialog after successful add
    setIsOpen(false);
  };

  const handleRemoveDate = (index: number) => {
    if (index < 0) return;
    const updatedDates = taggedDates.filter((_, i) => i !== index);
    updateMutation.mutate(updatedDates);
  };

  // Sort dates chronologically with earliest date first, latest date last
  const sortedDates = taggedDates ? [...taggedDates].sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return dateA.getTime() - dateB.getTime();
  }) : [];

  const displayedDates = sortedDates.slice(0, 2);
  const hasMoreDates = sortedDates.length > 2;

  return (
    <Dialog open={isOpen} onOpenChange={setIsOpen}>
      <DialogTrigger asChild>
        <div className="cursor-pointer min-h-[40px] p-1 relative">
          {/* Scrollable container for timing entries */}
          <div className="max-h-[60px] overflow-y-auto space-y-1 scrollbar-thin scrollbar-thumb-gray-600 scrollbar-track-transparent">
            {sortedDates.map((taggedDate, index) => (
              <div key={taggedDate.id || `${taggedDate.tag}:${taggedDate.date}:${index}`} className="grid grid-cols-[80px_1fr] gap-2 items-center text-xs">
                <Badge
                  variant="secondary"
                  className={`text-white ${getDynamicColor(taggedDate, projectStatus, taskStatus)} border-none px-2 py-0.5 text-center w-full`}
                >
                  {taggedDate.tag.toUpperCase()}
                </Badge>
                <span className="text-gray-300">
                  {format(new Date(taggedDate.date), "MMM dd")}
                </span>
              </div>
            ))}
          </div>
          
          {!taggedDates?.length && (
            <div className="text-xs text-muted-foreground flex items-center">
              <Calendar className="w-3 h-3 mr-1 text-white" />
              Add timing
            </div>
          )}
        </div>
      </DialogTrigger>
      
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{entityType === 'project' ? 'Project' : 'Sequence'} Timing</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4">
          {/* Existing dates */}
          <div className="space-y-2">
            {sortedDates.map((taggedDate, index) => (
              <div key={taggedDate.id || `${taggedDate.tag}:${taggedDate.date}:${index}`} className="flex items-center justify-between p-2 border rounded">
                <div className="flex items-center gap-2">
                  <Badge className={`text-white ${getDynamicColor(taggedDate, projectStatus, taskStatus)} border-none`}>
                    {taggedDate.tag.toUpperCase()}
                  </Badge>
                  <span className="text-sm">
                    {format(new Date(taggedDate.date), "MMM dd, yyyy")}
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleRemoveDate(taggedDate.id
                    ? taggedDates.findIndex(d => d.id === taggedDate.id)
                    : taggedDates.findIndex(d => d === taggedDate))}
                  className="text-red-500 hover:text-red-700"
                >
                  Remove
                </Button>
              </div>
            ))}
          </div>

          {/* Add new date */}
          <div className="border-t pt-4 space-y-3">
            <h4 className="font-medium text-sm">Add New Date</h4>
            <Select value={newTag} onValueChange={setNewTag}>
              <SelectTrigger>
                <SelectValue placeholder="Select tag type" />
              </SelectTrigger>
              <SelectContent>
                {tagOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>
                    <div className="flex items-center gap-2">
                      <div className={`w-3 h-3 rounded ${option.color}`} />
                      <div className="flex flex-col">
                        <span>{option.label}</span>
                        {(option.value === "deadline" || option.value === "due-date") && (
                          <span className="text-xs text-gray-500">Color changes based on date</span>
                        )}
                      </div>
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <Input
              type="date"
              value={newDate}
              onChange={(e) => setNewDate(e.target.value)}
              placeholder="Select date"
              style={{
                colorScheme: 'dark'
              } as React.CSSProperties}
            />
            
            <Button
              onClick={handleAddDate}
              disabled={!newTag || !newDate || updateMutation.isPending}
              className="w-full"
            >
              <Plus className="w-4 h-4 mr-1" />
              Add Date
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}